/**
 * Date utility functions for todo date/time handling
 *
 * IMPORTANT: All dates are stored and displayed in LOCAL timezone.
 * We avoid UTC conversion to prevent timezone-related date shifts.
 *
 * These functions use STRING PARSING instead of new Date() to avoid
 * browser-dependent timezone interpretation issues.
 */

/**
 * Extract date and time parts from an ISO date string
 * Handles formats: "YYYY-MM-DD", "YYYY-MM-DDTHH:mm", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DDTHH:mm:ss.sssZ"
 * Always extracts the date/time as-is, ignoring any timezone suffix
 */
function extractDateTimeParts(isoDate: string): { date: string; time: string } {
  // Remove timezone suffix if present (Z, +00:00, +09:00, etc.)
  const withoutTz = isoDate.replace(/[Z]$/, '').replace(/[+-]\d{2}:\d{2}$/, '')

  // Split by 'T' to separate date and time
  const [datePart, timePart] = withoutTz.split('T')

  // Extract time (HH:mm) if present
  let time = ''
  if (timePart) {
    const timeMatch = timePart.match(/^(\d{2}):(\d{2})/)
    if (timeMatch) {
      time = `${timeMatch[1]}:${timeMatch[2]}`
    }
  }

  return { date: datePart, time }
}

/**
 * Convert ISO 8601 date to datetime-local format (YYYY-MM-DDTHH:mm)
 * Extracts date/time directly from string without Date object conversion
 */
export function toDateTimeLocal(isoDate: string | null): string {
  if (!isoDate) return ''
  const { date, time } = extractDateTimeParts(isoDate)
  if (!time || time === '00:00') {
    return `${date}T00:00`
  }
  return `${date}T${time}`
}

/**
 * Convert ISO 8601 date to date format (YYYY-MM-DD)
 * Extracts date directly from string without Date object conversion
 */
export function toDateOnly(isoDate: string | null): string {
  if (!isoDate) return ''
  const { date } = extractDateTimeParts(isoDate)
  return date
}

/**
 * Convert ISO 8601 date to time format (HH:mm)
 * Extracts time directly from string without Date object conversion
 */
export function toTimeOnly(isoDate: string | null): string {
  if (!isoDate) return ''
  const { time } = extractDateTimeParts(isoDate)
  // Return empty if no time or midnight (user likely didn't set a specific time)
  if (!time || time === '00:00') {
    return ''
  }
  return time
}

/**
 * Combine date and time strings into local ISO string format
 * Does NOT convert to UTC - keeps the local time as-is
 */
export function combineDateTime(date: string, time: string): string {
  if (!date) return ''
  if (!time) {
    // Date only - use midnight local time
    return `${date}T00:00:00`
  }
  // Combine date and time in local format
  return `${date}T${time}:00`
}

/**
 * Convert a local date string (YYYY-MM-DD) to ISO format for database storage
 * Keeps the date in local timezone
 */
export function dateToISOString(dateStr: string, timeStr?: string): string {
  if (!dateStr) return ''
  if (timeStr) {
    return `${dateStr}T${timeStr}:00`
  }
  return `${dateStr}T00:00:00`
}

/**
 * Parse date string as local date (not UTC) to avoid timezone issues
 * Handles both "YYYY-MM-DD" and "YYYY-MM-DDTHH:mm:ss" formats
 */
export function parseLocalDate(dateStr: string): Date {
  // Extract just the date part (YYYY-MM-DD)
  const datePart = dateStr.split('T')[0]
  const [year, month, day] = datePart.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * Parse date string as local datetime (preserving time if present)
 * Returns date with correct local time, not UTC-shifted
 */
export function parseLocalDateTime(dateStr: string): Date {
  const datePart = dateStr.split('T')[0]
  const [year, month, day] = datePart.split('-').map(Number)

  // Check if time part exists
  if (dateStr.includes('T')) {
    const timePart = dateStr.split('T')[1]
    const [hours, minutes] = timePart.split(':').map(Number)
    return new Date(year, month - 1, day, hours || 0, minutes || 0)
  }

  return new Date(year, month - 1, day)
}
